import {
  Filter,
  Loader2,
  MoreHorizontal,
  Trash2,
  Zap,
  Users,
} from "lucide-react";
import { Button } from "../../tailwind/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../tailwind/dropdown-menu";
import SegmentFilterBadges from "./SegmentFilterBadges";

const TABLE_HEADERS = ["Segment Name", "Filters", "Created", "Actions"];

function SegmentActionsMenu({ segment, onViewContacts, onUseInCampaign, onDeleteSegment }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onViewContacts(segment)}>
          <Users className="h-4 w-4 mr-2" /> View Contacts
        </DropdownMenuItem>
        <DropdownMenuItem
          className="font-medium text-emerald-600 dark:text-emerald-400"
          onClick={() => onUseInCampaign(segment)}
        >
          <Zap className="h-4 w-4 mr-2" /> Use in Campaign
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600 dark:text-red-400"
          onClick={() => onDeleteSegment(segment)}
        >
          <Trash2 className="h-4 w-4 mr-2" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function SegmentsTab({
  segments,
  loading = false,
  error = null,
  onRetry,
  onViewContacts,
  onUseInCampaign,
  onDeleteSegment,
  onCreateSegment,
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden min-h-[400px]">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-slate-800 flex justify-between items-start gap-3 bg-gray-50/50 dark:bg-slate-950/50">
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 dark:text-white text-lg">Segments</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Saved groups using Lead Status and Tags filters.
            </p>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 shrink-0">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading…
            </div>
          )}
        </div>

        {error && (
          <div className="mx-4 mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-4">
            <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        )}

        {loading && segments.length === 0 ? (
          <div className="flex items-center justify-center p-12 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading segments…
          </div>
        ) : segments.length > 0 ? (
          <>
            {/* Mobile — same columns as table, stacked */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-800">
              {segments.map((segment) => (
                <div key={segment.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      className="font-bold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 text-left min-w-0 truncate"
                      onClick={() => onViewContacts(segment)}
                    >
                      {segment.name}
                    </button>
                    <SegmentActionsMenu
                      segment={segment}
                      onViewContacts={onViewContacts}
                      onUseInCampaign={onUseInCampaign}
                      onDeleteSegment={onDeleteSegment}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-2 text-sm min-w-0">
                    <div className="min-w-0">
                      <span className="text-gray-400 dark:text-gray-500 text-xs block mb-1">
                        Filters
                      </span>
                      <SegmentFilterBadges filters={segment.filters} />
                    </div>
                    <div>
                      <span className="text-gray-400 dark:text-gray-500 text-xs block">
                        Created
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {segment.createdAt || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="bg-gray-50 dark:bg-slate-950 border-b border-gray-200 dark:border-slate-800">
                    {TABLE_HEADERS.map((header) => (
                      <th
                        key={header}
                        className={`py-3 px-4 lg:px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                          header === "Actions" ? "text-right" : "text-left"
                        }`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                  {segments.map((segment) => (
                    <tr
                      key={segment.id}
                      className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-4 px-4 lg:px-6">
                        <button
                          type="button"
                          className="font-bold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 text-left truncate max-w-[200px]"
                          onClick={() => onViewContacts(segment)}
                        >
                          {segment.name}
                        </button>
                      </td>
                      <td className="py-4 px-4 lg:px-6">
                        <SegmentFilterBadges filters={segment.filters} />
                      </td>
                      <td className="py-4 px-4 lg:px-6 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {segment.createdAt || "—"}
                      </td>
                      <td className="py-4 px-4 lg:px-6 text-right">
                        <SegmentActionsMenu
                          segment={segment}
                          onViewContacts={onViewContacts}
                          onUseInCampaign={onUseInCampaign}
                          onDeleteSegment={onDeleteSegment}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          !error && (
            <div className="flex flex-col items-center justify-center p-8 sm:p-12 text-center h-full">
              <div className="h-16 w-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <Filter className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                No segments saved yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-sm mt-2 mb-6 text-sm sm:text-base">
                Apply Lead Status or Tags filters on All Contacts, then click Save as Segment.
              </p>
              <Button variant="outline" onClick={onCreateSegment}>
                Create a segment
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
}
